import FileInterface, { FileResponseInterface } from "types/file";
import base64toBlob from "helpers/file/base64ToBlob";

const SESSION_EXPIRATION_DATE_KEY: string = "session_expiration_date";
const SESSION_LIFETIME: number = Number(process.env.REACT_APP_SESSION_LIFETIME);

function refreshExpirationDate() {
    const expirationDate = new Date();
    expirationDate.setTime(new Date().getTime() + 60000 * SESSION_LIFETIME);
    localStorage.setItem(SESSION_EXPIRATION_DATE_KEY, expirationDate.toISOString());
}

function clearSession() {
    localStorage.removeItem(SESSION_EXPIRATION_DATE_KEY);
}

function getSessionExpirationDate(): Date | null {
    const data = localStorage.getItem(SESSION_EXPIRATION_DATE_KEY);
    if (data) {
        return new Date(data);
    }
    return null;
}

function sessionIsExpired(): boolean {
    const expirationDate = getSessionExpirationDate();
    if (expirationDate) {
        return expirationDate < new Date();
    }
    return true;
}

const API_URL = process.env.REACT_APP_SERVER_URL;
const CSRF_COOKIE_PATH = "authentication/csrf-cookie";

export const SESSION_LOST_EVENT = "auth:session-expired";

let csrfRefreshPromise: Promise<void> | null = null;
let sessionCheck: Promise<void> | null = null;
let ignoreUnauthorized = 0;
let sessionEpoch = 0;
const inflight = new Set<AbortController>();

/**
 * Drop in-flight API calls and ignore their 401s.
 * Login rotates the session id; a late 401 from the previous page must not log the user out.
 */
export function suspendSessionExpiry(): void {
    ignoreUnauthorized += 1;
    sessionEpoch += 1;
    for (const controller of inflight) {
        controller.abort();
    }
    inflight.clear();
}

export function resumeSessionExpiry(): void {
    ignoreUnauthorized = Math.max(0, ignoreUnauthorized - 1);
}

function isSessionEndpoint(url: string): boolean {
    return (
        url.includes("authentication/fresh") ||
        url.includes("authentication/logout") ||
        url.includes("authentication/login") ||
        url.includes("authentication/csrf-cookie")
    );
}

/**
 * A single 401 is not proof the session is gone: one late response can fail
 * while the cookie from login is still valid. Confirm with /fresh, once.
 */
function confirmSession(): void {
    if (sessionCheck || ignoreUnauthorized > 0 || sessionIsExpired()) {
        return;
    }

    const epoch = sessionEpoch;
    sessionCheck = (async () => {
        try {
            const res = await window.fetch(`${API_URL ?? ""}authentication/fresh`, {
                method: "GET",
                credentials: "include",
                cache: "no-store",
                headers: { accept: "application/json", "cache-control": "no-cache" },
            });

            if (res.status === 401 && epoch === sessionEpoch && ignoreUnauthorized === 0) {
                clearSession();
                window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT));
            }
        } catch {
            // A network error is not an expired session.
        } finally {
            sessionCheck = null;
        }
    })();
}

/**
 * Fetches a new CSRF cookie from Sanctum (GET /api/authentication/csrf-cookie).
 * Safe to call multiple times; concurrent calls are deduplicated.
 */
export function refreshCsrfToken(): Promise<void> {
    if (csrfRefreshPromise) {
        return csrfRefreshPromise;
    }

    csrfRefreshPromise = (async () => {
        const baseUrl = API_URL ?? "";
        const res = await window.fetch(`${baseUrl}${CSRF_COOKIE_PATH}`, {
            method: "GET",
            credentials: "include",
            // Avoid any intermediary caching of the CSRF cookie endpoint.
            cache: "no-store",
            headers: { "cache-control": "no-cache" },
        });

        if (!res.ok) {
            throw new Error(`CSRF cookie refresh failed with status ${res.status}`);
        }
    })().finally(() => {
        csrfRefreshPromise = null;
    });

    return csrfRefreshPromise;
}

export interface HttpResponse<T> {
    data: T;
    status: number;
    headers: Headers;
    config: {
        method: string;
        url: string;
    };
    raw: Response;
}

export interface HttpError extends Error {
    response?: HttpResponse<unknown>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function buildQueryString(params: Record<string, unknown> | undefined): string {
    if (!params) return "";
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
            for (const v of value) search.append(key, String(v));
        } else {
            search.set(key, String(value));
        }
    }
    return search.toString();
}

async function parseJsonSafely(res: Response): Promise<unknown> {
    const contentType = res.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
        return await res.json();
    }
    return await res.text();
}

function readCookie(name: string): string | undefined {
    const cookies = typeof document === "undefined" ? "" : document.cookie;
    if (!cookies) return undefined;
    const parts = cookies.split("; ");
    for (const part of parts) {
        const eqIdx = part.indexOf("=");
        if (eqIdx === -1) continue;
        const k = part.slice(0, eqIdx);
        if (k === name) return part.slice(eqIdx + 1);
    }
    return undefined;
}

class fetch {
    sessionIsExpired() {
        return sessionIsExpired();
    }

    clearSession() {
        return clearSession();
    }

    get<T = unknown>(
        url: string,
        config: HashMap<unknown> = {},
        external: boolean = false
    ): CancellablePromise<HttpResponse<T>> {
        return this.request<T>("GET", url, undefined, config, external);
    }

    delete<T = unknown>(url: string, config: HashMap<unknown> = {}): CancellablePromise<HttpResponse<T>> {
        return this.request<T>("DELETE", url, undefined, config, false);
    }

    post<T = unknown>(url: string, data?: unknown, config: HashMap<unknown> = {}): CancellablePromise<HttpResponse<T>> {
        return this.request<T>("POST", url, data, config, false);
    }

    put<T = unknown>(url: string, data?: unknown, config: HashMap<unknown> = {}): CancellablePromise<HttpResponse<T>> {
        return this.request<T>("PUT", url, data, config, false);
    }

    patch<T = unknown>(url: string, data: unknown, config: HashMap<unknown> = {}): CancellablePromise<HttpResponse<T>> {
        return this.request<T>("PATCH", url, data, config, false);
    }

    private request<T>(
        method: string,
        url: string,
        data: unknown,
        config: HashMap<unknown>,
        external: boolean
    ): CancellablePromise<HttpResponse<T>> {
        const controller = new AbortController();
        inflight.add(controller);

        const baseUrl = external ? "" : API_URL ?? "";
        const qs = buildQueryString(config?.params as Record<string, unknown> | undefined);
        const fullUrl = `${baseUrl}${url}${qs ? `${url.includes("?") ? "&" : "?"}${qs}` : ""}`;

        const credentials: RequestCredentials =
            typeof config?.withCredentials === "boolean"
                ? config.withCredentials
                    ? "include"
                    : "omit"
                : external
                  ? "omit"
                  : "include";

        let body: BodyInit | undefined = undefined;

        if (data !== undefined && method !== "GET") {
            if (data instanceof FormData) {
                body = data;
            } else if (typeof data === "string" || data instanceof Blob) {
                body = data as BodyInit;
            } else if (isPlainObject(data) || Array.isArray(data)) {
                body = JSON.stringify(data);
            } else {
                body = String(data);
            }
        }

        const buildHeaders = (): Headers => {
            const headers = new Headers((config?.headers ?? {}) as HeadersInit);

            if (data !== undefined && method !== "GET" && !headers.has("content-type")) {
                if (!(data instanceof FormData) && typeof data !== "string" && !(data instanceof Blob)) {
                    headers.set("content-type", "application/json");
                }
            }

            if (!external && credentials === "include" && !headers.has("X-XSRF-TOKEN")) {
                const shouldAttachXsrfHeader = !["GET", "HEAD", "OPTIONS"].includes(method);
                if (shouldAttachXsrfHeader) {
                    const xsrf = readCookie("XSRF-TOKEN");
                    if (xsrf) {
                        headers.set("X-XSRF-TOKEN", decodeURIComponent(xsrf));
                    }
                }
            }

            return headers;
        };

        const promise = (async () => {
            try {
                for (let attempt = 0; attempt < 2; attempt++) {
                    const res = await window.fetch(fullUrl, {
                        method,
                        headers: buildHeaders(),
                        body,
                        signal: controller.signal,
                        credentials,
                    });

                    const parsed = (await parseJsonSafely(res)) as unknown as T;
                    const response: HttpResponse<T> = {
                        data: parsed,
                        status: res.status,
                        headers: res.headers,
                        config: { method: method.toLowerCase(), url: fullUrl },
                        raw: res,
                    };

                    if (res.ok) {
                        refreshExpirationDate();
                        return response;
                    }

                    if (res.status === 419 && attempt === 0 && !external) {
                        await refreshCsrfToken();
                        continue;
                    }

                    const err = new Error(`Request failed with status ${res.status}`) as HttpError;
                    err.response = response;
                    if (
                        res.status === 401 &&
                        !external &&
                        ignoreUnauthorized === 0 &&
                        !isSessionEndpoint(url)
                    ) {
                        confirmSession();
                    }
                    throw err;
                }

                throw new Error("Request failed after CSRF token refresh");
            } finally {
                inflight.delete(controller);
            }
        })() as CancellablePromise<HttpResponse<T>>;

        promise.cancel = () => controller.abort();
        return promise;
    }
}

const singleton = new fetch();
export default singleton;

function wrapPromiseParse<T, R>(
    fetcher: CancellablePromise<HttpResponse<T>>,
    callback: (response: HttpResponse<T>) => R
): CancellablePromise<R> {
    const promise = fetcher.then((response) => {
        return callback(response);
    }) as CancellablePromise<R>;

    promise.cancel = fetcher.cancel;

    return promise;
}

export function parseResponse<T>(fetcher: CancellablePromise<HttpResponse<{ data: T }>>): CancellablePromise<T> {
    return wrapPromiseParse(fetcher, (response) => {
        return response.data.data;
    });
}

export function parseFileResponse(
    fetcher: CancellablePromise<HttpResponse<{ data: FileResponseInterface }>>
): CancellablePromise<FileInterface> {
    return wrapPromiseParse(fetcher, (response) => {
        const data = { ...response.data.data };
        return Object.assign({}, data, { content: base64toBlob(data.content, data.type) });
    });
}

interface ListResponse<T> {
    data: Array<T>;
    pager: {
        total: number;
        total_page: number;
        last_page: number;
        per_page: number;
        current_page: number;
        order?: string;
        direction?: string;
        custom?: HashMap<number | undefined>;
    };
    filters: HashMap<unknown>;
}

export function parseListResponse<T>(fetcher: CancellablePromise<HttpResponse<ListResponse<T>>>) {
    return wrapPromiseParse(fetcher, (response) => {
        const payload = response.data;
        return Object.assign([...payload.data], {
            active_filters: Object.assign({}, payload.filters),
            pager: payload.pager,
        });
    });
}
